import useSWR from "swr";

import { listDealsApi } from "src/api/deal";
import type { ListDealsRequestContent } from "@equaliq/api-model-v2";
import { useSelectedOrgId } from "@/hooks/common/useSelectedOrgId";
import type { ContractIntelligenceRecent } from "@/types/contractIntelligence.types";

/**
 * Production "Recently analyzed" list for the Contract Intelligence sidebar.
 *
 * Wires the REAL deal list (POST /deals/list via listDealsApi) — no new
 * endpoint, no fabricated rows. The intent is: surface the user's deals that
 * carry a completed DealAnalysis, newest-first, mapped to the
 * ContractIntelligenceRecent shape the demo list already renders.
 *
 * DATA-AVAILABILITY (backend enriched 2026-06-08):
 *   The /deals/list projection now carries, per deal, untyped-passthrough
 *   fields alongside the bare Deal record: a `title` (from the current
 *   DealVersion content) and `latestAnalysis: { status, createdAt,
 *   dealAnalysisId } | null` (the most recent DealAnalysis). Backend:
 *   equal-iq-ea/src/api-server/handlers/deals.ts listDeals projection. These
 *   are NOT on the typed api-model `Deal` schema (Path A passthrough), so we
 *   read them via a cast at the boundary below — same pattern the single-deal
 *   /deals/get projection uses (latestAnalysis.status — useDealIntermediateData.ts:139).
 *
 *   The engine writes DealAnalysis.status = 'complete' on the final stage
 *   (deal-context-access/internal/record-analysis-result.ts:11,58), so
 *   "completed" == status 'complete'. type/risk are NOT available on the list
 *   projection, so we use neutral defaults rather than fabricating a risk
 *   level (a real "high" alarm would be a lie).
 *
 * Maps from: ListDealsResponseContent.deals (api-model-v2, + passthrough
 *            title/latestAnalysis) -> ContractIntelligenceRecent[]
 */
export function useRecentlyAnalyzedContracts(): {
  recent: ContractIntelligenceRecent[];
  isLoading: boolean;
  /** Why the list is empty despite a successful fetch, when applicable.
   *  Lets the caller render an honest empty-state instead of guessing. */
  unavailableReason: string | null;
} {
  // Org-scope the fetch exactly like useDealOperations: /deals/list is scoped by
  // the request body's `orgId` (apiClient injects only the Clerk JWT, no
  // selected-org header), so we must pass it AND key SWR on it — otherwise
  // switching workspaces would serve the previous org's cached list.
  const orgId = useSelectedOrgId();
  const { data, isLoading } = useSWR(
    orgId ? ["recently-analyzed-contracts", orgId] : null,
    () => {
      const request: ListDealsRequestContent = { limit: 50 };
      if (orgId) {
        request.orgId = orgId;
      }
      return listDealsApi(request);
    },
    {
      revalidateOnFocus: false,
      onError: (err) => {
        console.error("Error fetching deals for recently-analyzed list:", err);
      },
    },
  );

  const dealMap = data?.deals ?? {};
  const dealCount = Object.keys(dealMap).length;

  // ── Mapping block ─────────────────────────────────────────────────────────
  // The list projection now carries `title` + `latestAnalysis` per deal as
  // untyped passthrough (see header note). The typed ListDealsResponseContent
  // doesn't model them, so we cast each row at the boundary (Path A).
  type EnrichedDeal = {
    latestAnalysis?: {
      status?: string;
      createdAt?: string;
      dealAnalysisId?: string;
      // Filename-slug the analysis viewer's /getContract resolves on (200),
      // unlike dealAnalysisId (a UUID that 404s). Backend now projects this on
      // the list latestAnalysis (deals.ts listDeals), mirroring getDeal:488.
      primaryContractId?: string;
    } | null;
    title?: string | null;
  };
  // Short "Mon DD" to match the existing recent-row date style.
  const fmtDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const recent: ContractIntelligenceRecent[] = Object.values(dealMap)
    .map((d) => d as unknown as EnrichedDeal)
    // Only deals whose latest analysis the engine marked complete.
    .filter((d) => d.latestAnalysis?.status === "complete")
    // Newest analysis first.
    .sort((a, b) =>
      (b.latestAnalysis?.createdAt ?? "").localeCompare(
        a.latestAnalysis?.createdAt ?? "",
      ),
    )
    .map((d): ContractIntelligenceRecent => ({
      name: (d.title && d.title.trim()) || "Untitled deal",
      date: fmtDate(d.latestAnalysis?.createdAt ?? ""),
      // type/risk aren't on the list projection — neutral, honest defaults
      // (no fabricated risk severity; "low" is the calmest of the enum).
      type: "Contract",
      color: "#6b7280", // neutral slate — not a category accent
      risk: "low",
      // Open the completed analysis at /analysis/:contractId. Must be the
      // filename-SLUG primaryContractId (which /getContract resolves 200), NOT
      // dealAnalysisId (a UUID that 404s). Backend projects primaryContractId
      // on the list latestAnalysis the same way getDeal does (deals.ts:488).
      contractId: d.latestAnalysis?.primaryContractId,
    }))
    .slice(0, 8);
  // ──────────────────────────────────────────────────────────────────────────

  const unavailableReason =
    recent.length === 0 && dealCount > 0
      ? "No deals with a completed analysis yet."
      : null;

  return { recent, isLoading, unavailableReason };
}
